import React, { useState, useEffect } from 'react';
import './FlagPriceSheet.css';
import { submitPriceFlag } from '../utils/db';

// Accepts either:
//   product + stores  → single-product mode (from PriceComparison)
//   products + stores → multi-product mode (from basket nudge)
function FlagPriceSheet({ isOpen, onClose, product, products, stores, defaultStoreId }) {
  // In multi-product mode the user picks the product first
  const multiMode = !product && products && products.length > 0;

  const [productId,  setProductId]  = useState('');
  const [storeId,    setStoreId]    = useState('');
  const [price,      setPrice]      = useState('');
  const [where,      setWhere]      = useState(''); // '' | 'store' | 'online'
  const [submitted,  setSubmitted]  = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Reset form each time sheet opens
  useEffect(() => {
    if (isOpen) {
      setProductId(multiMode ? (products[0]?.id || '') : '');
      setStoreId(defaultStoreId || stores[0]?.id || '');
      setPrice('');
      setWhere('');
      setSubmitted(false);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeProduct = multiMode
    ? products.find(p => p.id === productId) || products[0]
    : product;

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);

    // Resolve the exact retailer doc path from the product's prices array.
    // price.id = the retailer doc ID (set by db.js when building the prices array).
    // Falls back to null — admin dashboard handles null-path reviews as manual-verify.
    const matchingPrice = (activeProduct?.prices || []).find(p => p.storeId === storeId);
    const retailerDocPath = (activeProduct?.id && matchingPrice?.id)
      ? `products/${activeProduct.id}/retailers/${matchingPrice.id}`
      : null;

    await submitPriceFlag({
      productId:   activeProduct?.id   || null,
      productName: activeProduct?.name || null,
      storeId:     storeId             || null,
      retailerDocPath,
      price:       price || null,
      where:       where || null,
    });
    setSubmitting(false);
    setSubmitted(true);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="flag-backdrop" onClick={onClose} />

      {/* Sheet */}
      <div className="flag-sheet" role="dialog" aria-modal="true" aria-label="Flag this price">
        <div className="flag-handle" aria-hidden="true" />

        {/* Header */}
        <div className="flag-header">
          <span className="flag-title">Flag this price</span>
          <button className="flag-close" onClick={onClose} aria-label="Close">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="1" y1="1" x2="11" y2="11"/>
              <line x1="11" y1="1" x2="1" y2="11"/>
            </svg>
          </button>
        </div>

        {!submitted ? (
          <div className="flag-body">

            {/* Product — read-only (single) or picker (multi) */}
            {multiMode ? (
              <>
                <label className="flag-label" htmlFor="flag-product">Which product?</label>
                <select
                  id="flag-product"
                  className="flag-select"
                  value={productId}
                  onChange={e => setProductId(e.target.value)}
                >
                  {products.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </>
            ) : (
              <div className="flag-product-name">{product?.name}</div>
            )}

            {/* Store */}
            <label className="flag-label" htmlFor="flag-store">Which store?</label>
            <select
              id="flag-store"
              className="flag-select"
              value={storeId}
              onChange={e => setStoreId(e.target.value)}
            >
              {stores.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>

            {/* Price — optional */}
            <label className="flag-label" htmlFor="flag-price">
              Remember what the price was?
              <span className="flag-optional"> Optional</span>
            </label>
            <div className="flag-price-wrap">
              <span className="flag-currency">£</span>
              <input
                id="flag-price"
                type="number"
                min="0"
                step="0.01"
                className="flag-price-input"
                value={price}
                onChange={e => setPrice(e.target.value)}
                placeholder="0.00"
              />
            </div>

            {/* Where — optional toggle */}
            <label className="flag-label">
              Where did you see it?
              <span className="flag-optional"> Optional</span>
            </label>
            <div className="flag-where-row">
              <button
                className={`flag-where-btn${where === 'store' ? ' active' : ''}`}
                onClick={() => setWhere(w => w === 'store' ? '' : 'store')}
              >
                In store
              </button>
              <button
                className={`flag-where-btn${where === 'online' ? ' active' : ''}`}
                onClick={() => setWhere(w => w === 'online' ? '' : 'online')}
              >
                Online
              </button>
            </div>

            <button className="flag-submit" onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Submitting…' : 'Flag price'}
            </button>

          </div>
        ) : (
          /* Success state */
          <div className="flag-body flag-success">
            <p className="flag-thanks">Thanks — I'll take another look at this.</p>
            <button className="flag-done-btn" onClick={onClose}>Done</button>
          </div>
        )}
      </div>
    </>
  );
}

export default FlagPriceSheet;
